<template>
  <auth-layout>
    <h1 class="pop-auth__h1">Create your POP account</h1>
    <p class="pop-auth__sub">You are signing up with {{ providerName }}. Your email is already verified, so there is no code to enter.</p>

    <div class="social-confirm__card">
      <img v-if="profile.pictureUrl" :src="profile.pictureUrl" alt="" class="social-confirm__avatar" referrerpolicy="no-referrer" />
      <div v-else class="social-confirm__avatar social-confirm__avatar--initial">{{ initial }}</div>
      <div class="social-confirm__who">
        <div class="social-confirm__name">{{ profile.name || profile.email }}</div>
        <div class="social-confirm__email">
          <span>{{ profile.email }}</span>
          <span v-if="profile.isPrivateRelay" class="social-confirm__relay">Hide My Email</span>
        </div>
      </div>
      <span class="social-confirm__provider">{{ providerName }}</span>
    </div>
    <div class="social-confirm__links">
      <router-link class="pop-auth__link" to="/register">Not you? Switch account</router-link>
      <router-link class="pop-auth__link" :to="{ path: '/register', query: { email: profile.email } }">Use email instead</router-link>
    </div>

    <div v-if="error" class="pop-auth__box pop-auth__box--danger">{{ error }}</div>
    <button type="button" class="pop-auth__btn pop-auth__btn--primary" :disabled="busy" data-test="social-confirm" @click="confirm">
      {{ busy ? 'Creating your account' : 'Create account' }}
    </button>
    <p class="pop-auth__fine" style="margin-top: 16px">
      {{ providerName === 'Apple' ? 'Your name comes from Apple. Apple does not share a picture, so POP uses your initial.' : 'Your name and picture come from Google.' }}
      You can change them later in Account settings.
    </p>
    <template #footer><auth-terms-footer /></template>
  </auth-layout>
</template>

<script>
import AuthLayout from '../AuthLayout.vue'
import AuthTermsFooter from '../AuthTermsFooter.vue'
import { finishLogin, trackGoogleSignupConversion } from '../session'
import { getDeviceId } from '@/utils/device-id'
import { segmentPage } from '@/utils'

export default {
  name: 'SocialConfirmPage',
  components: { AuthLayout, AuthTermsFooter },
  data() {
    return { busy: false, error: '' }
  },
  computed: {
    signup() {
      return this.$store.state.signup
    },
    social() {
      return this.signup.social || {}
    },
    profile() {
      return this.social.profile || {}
    },
    providerName() {
      return this.profile.provider === 'apple' ? 'Apple' : 'Google'
    },
    initial() {
      return ((this.profile.name || this.profile.email || 'P')[0] || 'P').toUpperCase()
    },
  },
  created() {
    if (!this.social.socialToken) {
      this.$router.replace('/register')
      return
    }
    segmentPage('SignupSocialConfirm', { provider: this.profile.provider })
  },
  methods: {
    async confirm() {
      this.busy = true
      this.error = ''
      try {
        const response = await this.$store.dispatch('socialConfirm', {
          socialToken: this.social.socialToken,
          promoCode: this.signup.promoCode || this.signup.affiliateCode || '',
          servicePackage: this.signup.plan ? this.signup.plan.code : '',
          utm: this.signup.utm || null,
          deviceId: getDeviceId(),
        })
        const data = response.data || {}
        if (data.action === 'SIGNUP_COMPLETE') {
          trackGoogleSignupConversion()
          this.$store.commit('signup/patch', { social: null })
          finishLogin(this, { token: data.token, user: data.user }, {
            event: 'signup_verified', eventData: { method: this.profile.provider, entry: this.signup.entry },
            redirect: this.signup.plan && this.signup.plan.code ? '/plans/checkout' : '/plans',
          })
        } else if (data.action === 'PENDING_INVALID') {
          this.$notify({ group: 'info', type: 'warning', text: 'This sign-in expired. Please start again.' })
          this.$store.commit('signup/patch', { social: null })
          this.$router.replace('/register')
        } else if (data.action === 'ACCOUNT_EXISTS') {
          this.error = 'An account with this email was just created. Log in instead.'
        } else {
          this.error = data.msg || 'Could not create your account. Please try again.'
        }
      } catch (e) {
        console.log(e)
        this.error = 'Could not create your account. Please try again.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '../auth-tokens.scss';

.social-confirm {
  &__card { display: flex; align-items: center; gap: 14px; padding: 16px; border: 1px solid $pop-field-border; border-radius: 12px; background: #fff; }
  &__avatar { width: 48px; height: 48px; border-radius: 50%; flex: none; object-fit: cover; }
  &__avatar--initial { background: $pop-success; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 19px; }
  &__who { flex: 1; min-width: 0; }
  &__name { font-weight: 800; font-size: 15px; color: $pop-teal; }
  &__email { font-size: 13px; color: $pop-ink-muted; display: flex; align-items: center; gap: 6px; min-width: 0; span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } }
  &__relay { flex: none; background: #f7f7f7; border: 1px solid $pop-hairline; border-radius: 4px; padding: 1px 6px; font-size: 11px; }
  &__provider { font-size: 12px; color: $pop-ink-muted; font-weight: 600; }
  &__links { display: flex; justify-content: space-between; margin-top: 10px; font-size: 12.5px; }
}
</style>
